import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import SignatureCanvas from 'react-signature-canvas';
import { viewQuote, signQuote } from '@saiens/shared/api/services/quote.service';
import { formatCurrency, formatDate } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { toast } from '@saiens/shared/stores';
import { CheckCircle, FileText, RotateCcw } from 'lucide-react';
import { buildSignPayload, isValidPhone } from './quote-sign-payload';

export function QuoteViewPage() {
  const { token } = useParams<{ token: string }>();
  const signatureRef = useRef<SignatureCanvas>(null);
  const [signedByName, setSignedByName] = useState('');
  const [signedByEmail, setSignedByEmail] = useState('');
  const [showSignature, setShowSignature] = useState(false);
  const [sameAsPartner, setSameAsPartner] = useState(true);
  const [invoiceCompanyName, setInvoiceCompanyName] = useState('');
  const [invoiceVat, setInvoiceVat] = useState('');
  const [invoiceAddress, setInvoiceAddress] = useState('');
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  const { data: quote, isLoading, error, refetch } = useQuery({
    queryKey: ['quote', token],
    queryFn: async () => {
      if (!token) throw new Error('Invalid token');
      const result = await viewQuote(token);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to load quote');
      }
      return result.data!;
    },
    enabled: !!token,
  });

  useEffect(() => {
    if (quote) {
      setInvoiceCompanyName(quote.partner_company_name || '');
      setInvoiceVat(quote.partner_vat || '');
      setInvoiceAddress(quote.partner_street || '');
      setInvoiceEmail(quote.partner_email || '');
      setContactName(quote.contact_name || '');
      setContactPhone(quote.contact_phone || '');
    }
  }, [quote]);

  const signMutation = useMutation({
    mutationFn: async () => {
      if (!token || !signatureRef.current) {
        throw new Error('Missing required data');
      }

      if (signatureRef.current.isEmpty()) {
        throw new Error('Please provide your signature');
      }

      const signatureData = signatureRef.current.toDataURL('image/png');

      const result = await signQuote(
        token,
        buildSignPayload({
          signature: signatureData,
          signedByName,
          signedByEmail,
          sameAsPartner,
          invoiceCompanyName,
          invoiceVat,
          invoiceAddress,
          invoiceEmail,
          needsContactInfo: quote?.needs_contact_info === true,
          contactName,
          contactPhone,
        })
      );

      if (!result.success) {
        throw new Error(result.error?.message || 'Signing failed');
      }

      return result.data;
    },
    onSuccess: () => {
      toast.success('簽署成功', '報價單已成功簽署');
      refetch();
    },
    onError: (error: Error) => {
      toast.error('簽署失敗', error.message);
    },
  });

  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  const needsContactInfo = quote?.needs_contact_info === true;
  const isContactPhoneInvalid =
    needsContactInfo && contactPhone.trim() !== '' && !isValidPhone(contactPhone);
  const canSubmitSignature =
    signedByName.trim() !== '' &&
    (!needsContactInfo ||
      (contactName.trim() !== '' && contactPhone.trim() !== '' && isValidPhone(contactPhone)));

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
        <div className="rounded-xl bg-white p-8 text-center shadow-lg">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <FileText className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">無法載入報價單</h1>
          <p className="mt-2 text-gray-500">
            {error instanceof Error ? error.message : '連結可能已失效或報價單不存在'}
          </p>
        </div>
      </div>
    );
  }

  // Change history block, shared by the signed and unsigned layouts
  const changeHistoryBlock =
    quote.changes && quote.changes.length > 0 ? (
      <div className="mb-4 rounded-xl bg-white p-4 shadow-sm">
        <h2 className="mb-3 font-semibold text-gray-900">變更歷程</h2>

        {quote.original_amount_total != null &&
          quote.original_amount_total !== quote.amount_total && (
            <p className="mb-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
              原始金額 {formatCurrency(quote.original_amount_total)} → 現行金額{' '}
              {formatCurrency(quote.amount_total)}
            </p>
          )}

        <div className="space-y-3">
          {quote.changes.map((change) => (
            <div
              key={change.sequence_no}
              className="border-b border-gray-100 pb-3 last:border-0 last:pb-0"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-gray-900">
                  第 {change.sequence_no} 次變更
                </span>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                    change.state === 'effective'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  {change.state === 'effective' ? '已生效' : '待生效'}
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {formatDate(change.approved_at || change.submitted_at)}
              </p>
              {change.reason && (
                <p className="mt-1 text-sm text-gray-700">{change.reason}</p>
              )}
              <p
                className={`mt-1 text-sm font-medium ${
                  change.amount_delta >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {change.amount_delta >= 0 ? '+' : ''}
                {formatCurrency(change.amount_delta)}
              </p>
            </div>
          ))}
        </div>
      </div>
    ) : null;

  // Already signed
  if (quote.is_signed) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-8">
        <div className="mx-auto max-w-lg">
          <div className="rounded-xl bg-white p-8 text-center shadow-lg">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">報價單已簽署</h1>
            <p className="mt-2 text-gray-500">
              簽署人: {quote.signed_by_name}
              <br />
              簽署時間: {formatDate(quote.signed_date)}
            </p>
          </div>

          {/* Quote summary */}
          <div className="mt-6 rounded-xl bg-white p-6 shadow-sm">
            <h2 className="mb-4 font-semibold text-gray-900">報價單資訊</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">報價單號</span>
                <span className="font-medium">{quote.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">總金額</span>
                <span className="font-medium text-primary">{formatCurrency(quote.amount_total)}</span>
              </div>
            </div>
          </div>

          <div className="mt-6">{changeHistoryBlock}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary px-4 py-6 text-white">
        <h1 className="text-xl font-bold">報價單確認</h1>
        <p className="mt-1 text-sm text-white/80">{quote.name}</p>
      </div>

      <div className="px-4 py-6">
        {/* Customer Info */}
        <div className="mb-4 rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 font-semibold text-gray-900">客戶資訊</h2>
          <div className="space-y-1 text-sm">
            <p><span className="text-gray-500">客戶：</span>{quote.partner_name}</p>
            {quote.partner_street && (
              <p><span className="text-gray-500">地址：</span>{quote.partner_street}</p>
            )}
            {quote.project_name && (
              <p><span className="text-gray-500">專案：</span>{quote.project_name}</p>
            )}
          </div>
        </div>

        {/* Order Lines */}
        <div className="mb-4 rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 font-semibold text-gray-900">報價明細</h2>
          <div className="space-y-3">
            {quote.order_lines.map((line) => (
              <div key={line.id} className="flex justify-between border-b border-gray-100 pb-2 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{line.product_name || line.name}</p>
                  <p className="text-xs text-gray-500">
                    {line.product_uom_qty} {line.product_uom}
                    {line.saiens_color && ` • ${line.saiens_color}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-medium">{formatCurrency(line.price_subtotal)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Amount */}
        <div className="mb-6 rounded-xl bg-white p-4 shadow-sm">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">小計</span>
              <span>{formatCurrency(quote.amount_untaxed)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">稅額</span>
              <span>{formatCurrency(quote.amount_tax)}</span>
            </div>
            <div className="flex justify-between border-t pt-2 text-lg font-bold">
              <span>總計</span>
              <span className="text-primary">{formatCurrency(quote.amount_total)}</span>
            </div>
          </div>
        </div>

        {/* Change History */}
        {changeHistoryBlock}

        {/* Note */}
        {quote.note && (
          <div className="mb-6 rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-2 font-semibold text-gray-900">備註</h2>
            <div className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: quote.note }} />
          </div>
        )}

        {/* Invoice Info Section */}
        <div className="mb-6 rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 font-semibold text-gray-900">發票開立資訊</h2>

          <label className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              checked={sameAsPartner}
              onChange={(e) => setSameAsPartner(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700">
              同報價對象（{quote.partner_name}）
            </span>
          </label>

          {!sameAsPartner && (
            <div className="space-y-3 border-t pt-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  公司抬頭
                </label>
                <input
                  type="text"
                  value={invoiceCompanyName}
                  onChange={(e) => setInvoiceCompanyName(e.target.value)}
                  placeholder="公司名稱"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  統一編號
                </label>
                <input
                  type="text"
                  value={invoiceVat}
                  onChange={(e) => setInvoiceVat(e.target.value)}
                  placeholder="8 碼統一編號"
                  maxLength={8}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  地址
                </label>
                <input
                  type="text"
                  value={invoiceAddress}
                  onChange={(e) => setInvoiceAddress(e.target.value)}
                  placeholder="公司地址"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  value={invoiceEmail}
                  onChange={(e) => setInvoiceEmail(e.target.value)}
                  placeholder="billing@example.com"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none"
                />
              </div>
              <p className="text-xs text-gray-400">※ 未填統編將開立個人發票(B2C)</p>
            </div>
          )}
        </div>

        {/* Signature Section */}
        {!showSignature ? (
          <button
            onClick={() => setShowSignature(true)}
            className="w-full rounded-xl bg-primary py-4 text-center font-medium text-white"
          >
            我已確認報價內容，進行簽署
          </button>
        ) : (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-4 font-semibold text-gray-900">電子簽名</h2>

            {/* Name input */}
            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                簽署人姓名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={signedByName}
                onChange={(e) => setSignedByName(e.target.value)}
                placeholder="請輸入您的姓名"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none"
              />
            </div>

            {needsContactInfo && (
              <div className="mb-4 border-t pt-4">
                <h3 className="font-semibold text-gray-900">聯絡人資料</h3>
                <p className="mt-1 text-sm text-gray-500">
                  請填寫方便聯繫訂金與工程事宜的聯絡人姓名與手機
                </p>
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      聯絡人姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      autoComplete="name"
                      placeholder="請輸入聯絡人姓名"
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      聯絡電話 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      inputMode="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      autoComplete="tel"
                      placeholder="請輸入手機或電話"
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none"
                    />
                    {isContactPhoneInvalid && (
                      <p className="mt-1 text-xs text-red-500">請輸入 8 到 15 碼數字</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Email input (optional) */}
            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Email (選填)
              </label>
              <input
                type="email"
                value={signedByEmail}
                onChange={(e) => setSignedByEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none"
              />
            </div>

            {/* Signature pad */}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                請在下方簽名
              </label>
              <div className="relative rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: 'w-full h-48',
                  }}
                  backgroundColor="rgb(249, 250, 251)"
                />
                <button
                  onClick={clearSignature}
                  className="absolute right-2 top-2 rounded-lg bg-white p-2 text-gray-500 shadow hover:text-gray-700"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Submit button */}
            <button
              onClick={() => signMutation.mutate()}
              disabled={signMutation.isPending || !canSubmitSignature}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 py-3 font-medium text-white hover:bg-green-700 disabled:bg-gray-300"
            >
              {signMutation.isPending ? (
                <>
                  <LoadingSpinner size="sm" />
                  簽署中...
                </>
              ) : (
                '確認簽署'
              )}
            </button>

            <button
              onClick={() => setShowSignature(false)}
              className="mt-2 w-full py-2 text-center text-sm text-gray-500"
            >
              返回檢視報價單
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
